/**
 * Hook: Send ecash — encode proofs into a token, display as QR/copy string.
 * No Nostr DM — user shares the token manually.
 */

import { useState, useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';

import { encodeToken, type CasheroToken, generateRequestId } from '@/lib/cashu/token';
import { selectProofsForAmount } from '@/lib/cashu/split';
import { addTransaction } from '@/lib/storage/wallet-store';
import { hashSecret } from '@/lib/cashu/proof';
import { removeProofsBySecrets } from '@/lib/storage/wallet-store';
import type { Proof } from '@/lib/cashu/proof';
import { createSpentProofEvent } from '@/lib/nostr/spent-proof';

export type SendState =
  | { step: 'idle' }
  | { step: 'preparing' }
  | { step: 'ready'; token: string; amount: number; proofs: Proof[]; selected: Proof[]; remaining: Proof[] }
  | { step: 'sent'; token: string; amount: number }
  | { step: 'error'; error: string };

export function useSendEcash() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [state, setState] = useState<SendState>({ step: 'idle' });

  /** Prepare a token for a given amount from available proofs */
  const prepareToken = useCallback(async (
    proofs: Proof[],
    amount: number,
    mintPubkey: string,
    memo?: string,
  ) => {
    try {
      setState({ step: 'preparing' });

      const { selected, remaining, totalSelected } = selectProofsForAmount(proofs, amount);

      if (selected.length === 0) {
        setState({ step: 'error', error: 'Insufficient balance' });
        return;
      }

      const token: CasheroToken = {
        mint: mintPubkey,
        unit: 'piconero',
        proofs: selected,
        memo,
      };

      const tokenString = encodeToken(token);
      setState({ step: 'ready', token: tokenString, amount, proofs, selected, remaining });

      return { tokenString, selected, remaining, totalSelected };
    } catch (err) {
      setState({ step: 'error', error: (err as Error).message });
    }
  }, []);

  /** Confirm tokens were sent: publish spent-proof, remove from wallet */
  const confirmSent = useCallback(async (
    tokenString: string,
    amount: number,
    selectedProofs: Proof[],
    mintPubkey: string,
    userPrivkey: string,
    _remainingProofs?: Proof[],
  ) => {
    try {
      // Publish spent proof to Nostr (encrypted to mint)
      const secretHashes = selectedProofs.map(p => hashSecret(p.secret));
      const spentProofEvent = createSpentProofEvent(secretHashes, mintPubkey, userPrivkey);
      await nostr.event(spentProofEvent);

      // Remove spent proofs from wallet
      await removeProofsBySecrets(selectedProofs.map(p => p.secret));

      // Record transaction
      await addTransaction({
        id: generateRequestId(),
        type: 'send',
        mintNpub: mintPubkey,
        amount,
        timestamp: Date.now(),
        status: 'confirmed',
        tokenString,
      });

      setState({ step: 'sent', token: tokenString, amount });
    } catch (err) {
      setState({ step: 'error', error: (err as Error).message });
    }
  }, [nostr]);

  const reset = useCallback(() => {
    setState({ step: 'idle' });
  }, []);

  return { state, prepareToken, confirmSent, reset };
}
